import React, { useState, useEffect } from "react";
import { Box, Button, Typography, TextField, InputAdornment, IconButton } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { login, verifyMobileLogin } from "../../api/api";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
    generateExternalLoginURL,
    EXTERNAL_LOGIN_CONFIG,
} from "../../config/externalLogin";
import Logo from "../../assests/logo.png";

const Login = () => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState(""); // For displaying error messages
    const navigate = useNavigate();

    const handleClickShowPassword = () => {
        setShowPassword(!showPassword);
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    useEffect(() => {
        const token = localStorage.getItem("token");

        if (token) {
            console.log("Token found, navigating to /home...");
            navigate("/home", { replace: true });
        }
    }, []);

    const handleLogin = async () => {
        const redirectURI = `${window.location.origin}/verify-admin`;
        const loginURL = generateExternalLoginURL("SMS", redirectURI);
        window.location.href = loginURL;
    };

    const handleLoginEmail = async () => {
        const redirectURI = `${window.location.origin}/verify-admin`;
        const loginURL = generateExternalLoginURL("EMAIL", redirectURI);
        window.location.href = loginURL;
    };

    const handleSubmitLogin = async () => {
        if (!username || !password) {
            setError("Username and Password are required");
            Swal.fire({
                icon: "warning",
                title: "Missing Information",
                text: "Username and Password are required",
                confirmButtonColor: "#3085d6",
                confirmButtonText: "OK",
            });
            return;
        }

        try {
            const response = await login({ username, password });
            console.log("Login response:", response.data);

            if (response.data.success === true) {
                // Store user info in localStorage
                localStorage.setItem("token", response.data.user.id);
                localStorage.setItem(
                    "user",
                    JSON.stringify(response.data.user)
                );
                localStorage.setItem("branch_id", response.data.user.branch_id);
                localStorage.setItem("client_id", response.data.user.client_id);
                localStorage.setItem(
                    "branch_name",
                    response.data.user.branch_name
                );

                // Show success message
                Swal.fire({
                    icon: "success",
                    title: "Login Successful!",
                    text: `Welcome back, ${response.data.user.user_name}!`,
                    confirmButtonColor: "#28a745",
                    confirmButtonText: "Continue",
                    timer: 2000,
                    timerProgressBar: true,
                    showConfirmButton: true,
                }).then(() => {
                    // Navigate to home page after success
                    window.location.href = "/home";
                });
            } else {
                setError("Invalid username or password");
                Swal.fire({
                    icon: "error",
                    title: "Login Failed",
                    text: "Invalid username or password",
                    confirmButtonColor: "#dc3545",
                    confirmButtonText: "Try Again",
                });
            }
        } catch (error) {
            if (error.response && error.response.status === 401) {
                // Handle unauthorized response
                setError("Invalid username or password");
                Swal.fire({
                    icon: "error",
                    title: "Authentication Failed",
                    text: "Invalid username or password. Please check your credentials.",
                    confirmButtonColor: "#dc3545",
                    confirmButtonText: "Try Again",
                });
            } else {
                setError("An error occurred. Please try again.");
                console.error("Error:", error);
                Swal.fire({
                    icon: "error",
                    title: "Connection Error",
                    text: "An error occurred while connecting to the server. Please try again.",
                    confirmButtonColor: "#dc3545",
                    confirmButtonText: "Retry",
                });
            }
        }
    };

    return (
        <Box
            sx={{
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                bgcolor: "#f5f5f5",
            }}
        >
            {/* Login Box */}
            <Box
                sx={{
                    width: { xs: "90%", sm: "400px" },
                    p: 4,
                    bgcolor: "white",
                    boxShadow: 3,
                    borderRadius: 2,
                    textAlign: "center",
                }}
            >
                {/* Logo */}
                <Box sx={{ mb: 3, display: "flex", justifyContent: "center" }}>
                    <img
                        src={Logo}
                        alt="Logo"
                        style={{
                            width: "200px",
                            maxWidth: "100%",
                            height: "auto"
                        }}
                    />
                </Box>
                <Typography variant="body1" sx={{ mb: 4, color: "gray" }}>
                    Welcome back! Please login to continue.
                </Typography>
                {/* Username Field */}
                <TextField
                    label="Username"
                    variant="outlined"
                    fullWidth
                    sx={{ mb: 2 }}
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                />
                <TextField
                    label="Password"
                    type={showPassword ? "text" : "password"}
                    variant="outlined"
                    fullWidth
                    sx={{ mb: 2 }}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />

                {/* Password Field */}
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    sx={{ py: 1.5, fontSize: "1rem", mb: 2 }}
                    onClick={handleSubmitLogin}
                >
                    Submit
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    fullWidth
                    onClick={handleLogin}
                    sx={{ py: 1.5, fontSize: "1rem", mb: 2 }}
                >
                    Sign in with Mobile Number
                </Button>

                {/* Login Button */}
                <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    fullWidth
                    onClick={handleLoginEmail}
                    sx={{ py: 1.5, fontSize: "1rem" }}
                >
                    Sign in with Email
                </Button>
            </Box>
        </Box>
    );
};

export default Login;
